import axios, {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  AxiosRequestConfig,
} from 'axios';
import get from 'lodash/get';
// import { BASE_URL } from '../constants/apiRoutes';
import { getAPIGatewayEndpoint } from './getAPIGatewayEndpoint';

/**
 * Interface for embedded error responses that come with HTTP 200 status
 * This can happen when backend proxies return 200 but body contains error info
 */
interface EmbeddedErrorResponse {
  timestamp?: string;
  path?: string;
  status?: number;
  error?: string;
  requestId?: string;
  message?: string;
}

/**
 * Custom error class for embedded authentication errors in HTTP 200 responses
 * This allows us to throw errors that trigger the existing retry mechanism
 */
class EmbeddedAuthError extends Error {
  public response: {
    status: number;
    data: EmbeddedErrorResponse;
    statusText: string;
  };

  constructor(embeddedError: EmbeddedErrorResponse, originalUrl: string) {
    super(
      `Embedded auth error in response from ${originalUrl}: ${embeddedError.error || 'Unauthorized'}`
    );
    this.name = 'EmbeddedAuthError';
    this.response = {
      status: embeddedError.status || 401,
      data: embeddedError,
      statusText: embeddedError.error || 'Unauthorized',
    };
  }
}

// Forward declaration to avoid circular dependency
let refreshAccessTokenFunction:
  | ((refreshToken: string) => Promise<void>)
  | null = null;
let startPKCEFlowFunction: (() => Promise<void>) | null = null;

// Callback for when max retries are exhausted
let onMaxRetriesExhaustedCallback: (() => void) | null = null;

// Function to set the SSO functions (called from SSO.ts)
export const setSSOFunctions = (
  refreshFn: (refreshToken: string) => Promise<void>,
  startPKCEFn: () => Promise<void>
) => {
  refreshAccessTokenFunction = refreshFn;
  startPKCEFlowFunction = startPKCEFn;
};

/**
 * Sets a callback to be called when API retries are exhausted (after 3 retries)
 * This is used to show an error modal to the user
 */
export const setOnMaxRetriesExhausted = (callback: () => void) => {
  onMaxRetriesExhaustedCallback = callback;
};

class HttpClient {
  private apiInstance: AxiosInstance;
  private dataExtractor: string;
  private isRefreshing: boolean = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error?: unknown) => void;
    config: AxiosRequestConfig;
  }> = [];

  constructor({ baseURL = '', dataExtractor = 'data.data' }) {
    this.apiInstance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-Deployment-Id': 'blue',
      },
    });
    this.dataExtractor = dataExtractor;
  }

  /**
   * Processes the failed queue after token refresh
   */
  private processQueue(error: unknown = null, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error);
      } else {
        if (token) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${token}`,
          };
        }
        resolve(this.apiInstance.request(config));
      }
    });

    this.failedQueue = [];
  }

  /**
   * Checks if the current token is close to expiry (within 5 minutes)
   */
  private isTokenNearExpiry(): boolean {
    const expiresAt = sessionStorage.getItem('pfz-cggenai-token-expiry');
    if (!expiresAt) return true;

    const expiryTime = parseInt(expiresAt, 10);
    const currentTime = Date.now();
    const fiveMinutesInMs = 5 * 60 * 1000;

    return currentTime >= expiryTime - fiveMinutesInMs;
  }

  /**
   * Checks if we have all required tokens
   */
  private hasValidTokens(): boolean {
    const accessToken = sessionStorage.getItem('pfz-cggenai-token-access');
    const idToken = sessionStorage.getItem('pfz-cggenai-token-id');
    const refreshToken = sessionStorage.getItem('pfz-cggenai-token-refresh');
    const expiresAt = sessionStorage.getItem('pfz-cggenai-token-expiry');

    return !!(accessToken && idToken && refreshToken && expiresAt);
  }

  /**
   * Attempts to refresh the token and retry failed requests
   */
  private async handleTokenRefresh(): Promise<string | null> {
    if (this.isRefreshing) {
      // If already refreshing, wait for it to complete
      return new Promise((resolve, reject) => {
        this.failedQueue.push({
          resolve: () => {
            const token = sessionStorage.getItem('pfz-cggenai-token-id');
            resolve(token);
          },
          reject,
          config: {} as AxiosRequestConfig, // Not used for queue resolution
        });
      });
    }

    this.isRefreshing = true;

    try {
      const refreshToken = sessionStorage.getItem('pfz-cggenai-token-refresh');

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      if (!refreshAccessTokenFunction) {
        throw new Error('Token refresh function not available');
      }

      // Check if refresh token might be expired (basic check)
      const tokenExpiry = sessionStorage.getItem('pfz-cggenai-token-expiry');
      if (tokenExpiry) {
        const expiryTime = parseInt(tokenExpiry, 10);
        const currentTime = Date.now();
        // If the token expired more than 7 days ago, likely the refresh token is also expired
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

        if (currentTime > expiryTime + sevenDaysInMs) {
          throw new Error('Tokens likely expired, need fresh authentication');
        }
      }

      await refreshAccessTokenFunction(refreshToken);

      const newToken = sessionStorage.getItem('pfz-cggenai-token-id');

      if (!newToken) {
        throw new Error('Failed to get new token after refresh');
      }

      // Verify that all tokens are present after refresh
      if (!this.hasValidTokens()) {
        throw new Error('Incomplete token refresh');
      }

      this.processQueue(null, newToken);
      return newToken;
    } catch (error) {
      this.processQueue(error, null);

      // If refresh fails, initiate re-authentication
      if (startPKCEFlowFunction) {
        // Clear all tokens before starting fresh authentication
        this.clearAllTokens();

        // Add a small delay to ensure session storage is cleared
        setTimeout(() => {
          if (startPKCEFlowFunction) {
            startPKCEFlowFunction().catch(authError => {
              // eslint-disable-next-line no-console
              console.error('Failed to start PKCE flow:', authError);
            });
          }
        }, 100);
      }

      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Clears all authentication tokens from session storage
   */
  private clearAllTokens(): void {
    const tokenKeys = [
      'pfz-cggenai-token-access',
      'pfz-cggenai-token-id',
      'pfz-cggenai-token-refresh',
      'pfz-cggenai-token-expiry',
      'pfz-cggenai-user-givenname',
      'pfz-cggenai-user-role',
      'pfz-cggenai-user-primary-language',
    ];

    tokenKeys.forEach(key => sessionStorage.removeItem(key));
  }

  /**
   * Checks if the error is a 401 unauthorized error
   */
  private is401Error(error: AxiosError): boolean {
    return error.response?.status === 401;
  }

  /**
   * Checks if the error is a network-related error
   */
  private isNetworkError(error: AxiosError): boolean {
    return !error.response && error.code !== 'ECONNABORTED';
  }

  /**
   * Checks if a successful HTTP response body contains an embedded authentication error
   * This handles the edge case where the backend returns HTTP 200 but the body contains a 401
   *
   * @param responseData - The response body data to check
   * @returns true if the response contains an embedded 401/403 error
   */
  private hasEmbeddedAuthError(
    responseData: unknown
  ): responseData is EmbeddedErrorResponse {
    if (!responseData || typeof responseData !== 'object') {
      return false;
    }

    const data = responseData as EmbeddedErrorResponse;

    // Check for embedded status code indicating auth error
    if (data.status === 401 || data.status === 403) {
      return true;
    }

    // Check for error field indicating unauthorized
    if (typeof data.error === 'string') {
      const errorLower = data.error.toLowerCase();
      if (errorLower === 'unauthorized' || errorLower === 'forbidden') {
        return true;
      }
    }

    return false;
  }

  /**
   * Logs when an embedded authentication error is detected for debugging purposes
   *
   * @param url - The URL that returned the embedded error
   * @param responseData - The response data containing the embedded error
   */
  private logEmbeddedAuthError(
    url: string,
    responseData: EmbeddedErrorResponse
  ): void {
    // eslint-disable-next-line no-console
    console.warn(
      '[HttpClient] Detected embedded auth error in HTTP 200 response:',
      {
        url,
        embeddedStatus: responseData.status,
        error: responseData.error,
        requestId: responseData.requestId,
      }
    );
  }

  /**
   * Checks if the error indicates the user should re-authenticate
   * Now also handles EmbeddedAuthError from HTTP 200 responses with embedded 401
   */
  private shouldReauthenticate(error: AxiosError | EmbeddedAuthError): boolean {
    // Handle embedded auth errors from HTTP 200 responses
    if (error instanceof EmbeddedAuthError) {
      return true;
    }
    // 401, 403, or specific error messages indicating invalid/expired tokens
    if (error.response?.status === 401 || error.response?.status === 403) {
      return true;
    }

    // Check for specific error messages that indicate token issues
    const errorData = error.response?.data as { message?: string } | undefined;
    const errorMessage = errorData?.message?.toLowerCase() || '';
    const tokenErrorIndicators = [
      'invalid token',
      'expired token',
      'token expired',
      'unauthorized',
      'invalid_token',
      'access_denied',
    ];

    return tokenErrorIndicators.some(indicator =>
      errorMessage.includes(indicator)
    );
  }

  /**
   * Retries a request with a new token
   */
  private async retryWithNewToken(
    originalConfig: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    const newToken = await this.handleTokenRefresh();

    if (!newToken) {
      throw new Error('Unable to refresh token');
    }

    // Update the authorization header with the new token
    const updatedConfig = {
      ...originalConfig,
      headers: {
        ...originalConfig.headers,
        Authorization: `Bearer ${newToken}`,
      },
    };

    return this.apiInstance.request(updatedConfig);
  }

  private async request<T>(
    method: 'get' | 'post' | 'delete' | 'patch' | 'put',
    url: string,
    data?: HTTPClientRequestPayload,
    headers?: HTTPClientRequestHeaders,
    responseType?: AxiosRequestConfig['responseType'],
    config?: AxiosRequestConfig,
    noAuthHeader = false,
    retryCount = 0
  ): Promise<unknown> {
    const token = sessionStorage.getItem('pfz-cggenai-token-id');

    // If no auth header is required, proceed normally
    if (noAuthHeader) {
      return this.executeRequest<T>(
        method,
        url,
        data,
        headers,
        responseType,
        config,
        {}
      );
    }

    // Check if token is near expiry and proactively refresh if needed
    if (
      token &&
      this.isTokenNearExpiry() &&
      !this.isRefreshing &&
      retryCount === 0
    ) {
      try {
        await this.handleTokenRefresh();
        // Get the new token after refresh
        const newToken = sessionStorage.getItem('pfz-cggenai-token-id');
        return this.executeRequest<T>(
          method,
          url,
          data,
          headers,
          responseType,
          config,
          newToken ? { Authorization: `Bearer ${newToken}` } : {}
        );
      } catch (refreshError) {
        // If proactive refresh fails, continue with existing token and let normal error handling take over
        // eslint-disable-next-line no-console
        console.warn(
          'Proactive token refresh failed, continuing with existing token:',
          refreshError
        );
      }
    }

    const authHeader: Record<string, string> = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    try {
      return await this.executeRequest<T>(
        method,
        url,
        data,
        headers,
        responseType,
        config,
        authHeader
      );
    } catch (error) {
      const axiosError = error as AxiosError | EmbeddedAuthError;
      const isEmbeddedAuthError = axiosError instanceof EmbeddedAuthError;
      const isAuthError = this.shouldReauthenticate(axiosError);

      // Handle embedded auth errors (HTTP 200 with 401 in body) - backend issue, just retry without token refresh
      if (isEmbeddedAuthError && retryCount < 3) {
        // eslint-disable-next-line no-console
        console.warn(
          `[HttpClient] Retrying request due to embedded auth error (attempt ${retryCount + 1}/3):`,
          { url }
        );

        // Simple retry without token refresh - token is valid, this is a backend issue
        return this.request(
          method,
          url,
          data,
          headers,
          responseType,
          config,
          noAuthHeader,
          retryCount + 1
        );
      }

      // Handle embedded auth errors when max retries exhausted
      if (isEmbeddedAuthError && retryCount >= 3) {
        // eslint-disable-next-line no-console
        console.error(
          '[HttpClient] Max retries exhausted for embedded auth error:',
          { url, retryCount }
        );
        if (onMaxRetriesExhaustedCallback) {
          onMaxRetriesExhaustedCallback();
        }
        return HttpClient.createErrorResponse(axiosError, retryCount);
      }

      // Handle regular 401/403 errors - these need token refresh
      if (
        isAuthError &&
        retryCount < 3 &&
        refreshAccessTokenFunction &&
        startPKCEFlowFunction
      ) {
        try {
          // If we're already refreshing, add to queue
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({
                resolve: () => {
                  // Retry the original request after refresh completes
                  this.request(
                    method,
                    url,
                    data,
                    headers,
                    responseType,
                    config,
                    noAuthHeader,
                    retryCount + 1
                  )
                    .then(resolve)
                    .catch(reject);
                },
                reject,
                config: {} as AxiosRequestConfig,
              });
            });
          }

          // Attempt to refresh token and retry
          await this.handleTokenRefresh();

          // Retry the original request with incremented retry count
          return this.request(
            method,
            url,
            data,
            headers,
            responseType,
            config,
            noAuthHeader,
            retryCount + 1
          );
        } catch (refreshError) {
          // If token refresh fails, return the original error
          // eslint-disable-next-line no-console
          console.error('Token refresh failed after 401 error:', refreshError);
          return HttpClient.createErrorResponse(axiosError, retryCount);
        }
      }

      // For non-auth errors or when retries are exhausted
      return HttpClient.createErrorResponse(axiosError, retryCount);
    }
  }

  /**
   * Executes the actual HTTP request
   */
  private async executeRequest<T>(
    method: 'get' | 'post' | 'delete' | 'patch' | 'put',
    url: string,
    data?: HTTPClientRequestPayload,
    headers?: HTTPClientRequestHeaders,
    responseType?: AxiosRequestConfig['responseType'],
    config?: AxiosRequestConfig,
    authHeader: Record<string, string> = {}
  ): Promise<unknown> {
    const commonOptions: AxiosRequestConfig = {
      ...config,
      headers: {
        ...headers,
        ...authHeader,
      },
    };

    if (responseType) {
      commonOptions.responseType = responseType;
    }

    let response: AxiosResponse<T>;

    if (method === 'get') {
      response = await this.apiInstance.get(url, {
        ...commonOptions,
        params: data,
      });
    } else if (method === 'delete') {
      response = await this.apiInstance.delete(url, {
        ...commonOptions,
        params: data,
      });
    } else if (method === 'patch') {
      response = await this.apiInstance.patch(url, data, commonOptions);
    } else if (method === 'put') {
      response = await this.apiInstance.put(url, data, commonOptions);
    } else {
      // Assuming the method is 'post'
      response = await this.apiInstance.post(url, data, commonOptions);
    }

    // Check for embedded authentication errors in HTTP 200 responses
    // Skip check for binary responses (arraybuffer/blob)
    if (
      responseType !== 'arraybuffer' &&
      responseType !== 'blob' &&
      this.hasEmbeddedAuthError(response.data)
    ) {
      const embeddedError = response.data as EmbeddedErrorResponse;
      this.logEmbeddedAuthError(url, embeddedError);

      // Throw an error that will be caught by the retry mechanism in request()
      throw new EmbeddedAuthError(embeddedError, url);
    }

    return this.createSuccessResponse(response);
  }

  /**
   * Public method to check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return this.hasValidTokens() && !this.isTokenNearExpiry();
  }

  /**
   * Public method to manually trigger token refresh
   */
  public async refreshToken(): Promise<boolean> {
    try {
      if (!refreshAccessTokenFunction) {
        return false;
      }

      const refreshToken = sessionStorage.getItem('pfz-cggenai-token-refresh');
      if (!refreshToken) {
        return false;
      }

      await refreshAccessTokenFunction(refreshToken);
      return this.hasValidTokens();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Manual token refresh failed:', error);
      return false;
    }
  }

  /**
   * Public method to clear authentication and logout
   */
  public logout(): void {
    this.clearAllTokens();
    if (startPKCEFlowFunction) {
      startPKCEFlowFunction().catch(error => {
        // eslint-disable-next-line no-console
        console.error('Failed to start authentication flow:', error);
      });
    }
  }

  public get<T>(
    url: string,
    params?: HTTPClientRequestPayload,
    headers?: HTTPClientRequestHeaders,
    responseType?: AxiosRequestConfig['responseType'],
    config?: AxiosRequestConfig,
    noAuthHeader?: boolean
  ): Promise<unknown> {
    return this.request<T>(
      'get',
      url,
      params,
      headers,
      responseType,
      config,
      noAuthHeader
    );
  }

  public delete<T>(
    url: string,
    params?: HTTPClientRequestPayload,
    headers?: HTTPClientRequestHeaders
  ): Promise<unknown> {
    return this.request<T>('delete', url, params, headers);
  }

  public post<T>(
    url: string,
    data?: HTTPClientRequestPayload,
    headers?: HTTPClientRequestHeaders
  ): Promise<unknown> {
    return this.request<T>('post', url, data, headers);
  }

  public put<T>(
    url: string,
    data?: HTTPClientRequestPayload,
    headers?: HTTPClientRequestHeaders,
    config?: AxiosRequestConfig,
    noAuthHeader?: boolean
  ): Promise<unknown> {
    return this.request<T>(
      'put',
      url,
      data,
      headers,
      undefined,
      config,
      noAuthHeader
    );
  }

  public patch<T>(
    url: string,
    data?: HTTPClientRequestPayload,
    headers?: HTTPClientRequestHeaders
  ): Promise<unknown> {
    return this.request<T>('patch', url, data, headers);
  }

  static createErrorResponse(
    error: AxiosError | EmbeddedAuthError,
    retryCount?: number
  ) {
    // Handle EmbeddedAuthError specifically
    if (error instanceof EmbeddedAuthError) {
      return {
        success: false,
        status: error.response.status,
        statusText: error.response.statusText,
        message: error.message,
        errors: [],
        errorDetails: '',
        data: null,
        retryCount: retryCount || 0,
        isAuthError: true,
        isEmbeddedAuthError: true,
      };
    }

    const errorResponse = {
      success: false,
      status: get(error, 'response.status', null),
      statusText: get(error, 'response.statusText', ''),
      message: get(error, 'response.data.message', error.message),
      errors: get(error, 'response.data.errors', []),
      errorDetails: get(error, 'response.data.errors.details', ''),
      data: get(error, 'response.data.data', null),
      retryCount: retryCount || 0,
      isAuthError:
        error.response?.status === 401 || error.response?.status === 403,
      isEmbeddedAuthError: false,
    };

    // Add additional context for network errors
    if (!error.response) {
      errorResponse.message =
        error.code === 'ECONNABORTED'
          ? 'Request timeout - please try again'
          : 'Network error - please check your connection';
    }

    return errorResponse;
  }

  private createSuccessResponse(res: AxiosResponse) {
    let data;

    if (get(res, 'config.responseType') === 'arraybuffer') {
      data = get(res, 'data', null);
    } else {
      data = get(res, this.dataExtractor, null);
    }

    return {
      success: get(res, 'data.success', true),
      status: get(res, 'status', null),
      statusText: get(res, 'statusText', ''),
      message: get(res, 'data.message', ''),
      headers: get(res, 'headers', ''),
      errors: get(res, 'data.errors', []),
      data,
    };
  }
}

// Exports
export const httpClient = new HttpClient({ baseURL: getAPIGatewayEndpoint() });
export type HTTPClientRequestPayload = AxiosRequestConfig['data'];
export type HTTPClientRequestHeaders = AxiosRequestConfig['headers'];
export type HTTPClientResponse = AxiosResponse;
